// eslint-disable-next-line max-classes-per-file
const q = require('queried');
const extend = require('xtend/mutable');
const on = require('emmy/on');
const forEach = require('object-foreach');
const offset = require('mucss/offset');
const scrollTo = require('scroll-to');
const kudago = require('./kudago');

const DEFAULT_ZOOM = 13;
const MAX_ZOOM = 16.5;

class Map {
  init() {
    throw new Error('Map#create have to be redefined in subclasses');
  }

  getCoords(address) {
    return new Promise((resolve) => {
      if (address.coords) {
        resolve(address);
      } else {
        this.getCoordsFromAddress(address, (coords) => {
          const addressWithCoords = extend({}, address, { coords });
          resolve(addressWithCoords);
        });
      }
    });
  }

  scrollToMap() {
    const map = q('#place-map');
    const to = offset(map);

    scrollTo(to.left, to.top - 100, {
      duration: 500,
    });
  }
}

const YANDEX_CONTROLS = ['zoomControl', 'fullscreenControl'];

class YandexMap extends Map {
  constructor(...args) {
    super(...args);
    this.provider = window.ymaps;
    this.objects = {};
  }

  init(element, addresses) {
    if (this.provider) {
      this.provider.ready(() => {
        this.initUtilClasses();

        // get all addressses coords firstly
        // eslint-disable-next-line max-len
        Promise.all(addresses.map((address) => this.getCoords(address))).then((addressesWithCoords) => {
          if (addressesWithCoords.length == 1) {
            this.map = new this.provider.Map('place-map', {
              center: addressesWithCoords[0].coords,
              zoom: DEFAULT_ZOOM,
              controls: YANDEX_CONTROLS,
            });
          } else {
            const sw = addressesWithCoords
              .map((address) => address.coords)
              .reduce((a1, a2) => [
                Math.min(a1[0], a2[0]),
                Math.min(a1[1], a2[1]),
              ]);

            const ne = addressesWithCoords
              .map((address) => address.coords)
              .reduce((a1, a2) => [
                Math.max(a1[0], a2[0]),
                Math.max(a1[1], a2[1]),
              ]);

            this.map = new this.provider.Map('place-map', {
              bounds: [sw, ne],
              controls: YANDEX_CONTROLS,
            });

            const zoom = Math.min(this.map.getZoom() - 1, MAX_ZOOM);
            this.map.setZoom(zoom);
          }

          on(window, 'resize', () => {
            this.map.container.fitToViewport();
          });

          addressesWithCoords.forEach((address) => {
            const object = this.createObject(address);
            this.map.geoObjects.add(object);
          });
        });
      });
    }
  }

  createObject(address) {
    const object = new this.provider.GeoObject({
      geometry: {
        type: 'Point',
        coordinates: address.coords,
      },
      properties: {
        title: address.title,
        description: address.address,
      },
    }, {
      balloonLayout: this.balloonLayout,
      balloonContentBodyLayout: this.balloonContentBodyLayout,
      balloonShadow: false,
      iconLayout: 'default#image',
      iconImageHref: `${kudago.staticBaseUrl}img/pins.png`,
      iconImageSize: [36, 37],
      iconImageOffset: [-12, -32],
      iconShadow: false,
    });

    this.objects[address.id] = object;
    return object;
  }

  getCoordsFromAddress(address, callback) {
    this.provider.geocode(`${address.location}, ${address.address}`, { results: 1 }).then((result) => {
      const firstGeoObject = result.geoObjects.get(0).geometry.getCoordinates();
      const coords = [firstGeoObject[0], firstGeoObject[1]];
      callback(coords);
    });
  }

  initUtilClasses() {
    const _this = this;

    _this.balloonContentBodyLayout = _this.provider.templateLayoutFactory.createClass(`<div class="map-balloon-content">
        <p class="map-balloon-content-title">$[properties.title]</p>
        <p class="map-balloon-content-description">$[properties.description]</p>
      </div>`, {
      build() {
        _this.balloonContentBodyLayout.superclass.build.call(this);
      },

      clear() {
        _this.balloonContentBodyLayout.superclass.clear.call(this);
      },
    });

    // Ballon style layout
    _this.balloonLayout = _this.provider.templateLayoutFactory.createClass(`<div class="map-balloon">
        $[[options.contentBodyLayout]]
        <a class="map-balloon-close-btn" href="#">&times;</a>
        <div class="map-balloon-tail"></div>
      </div>
      <div class="map-balloon-placemark"></div>`, {
      build() {
        this.constructor.superclass.build.call(this);

        const parentEl = this.getParentElement();

        this.element = parentEl.querySelector('.map-balloon');
        this.closeButton = parentEl.querySelector('.map-balloon-close-btn');
        this.tail = parentEl.querySelector('.map-balloon-tail');
        this.placemark = parentEl.querySelector('.map-balloon-placemark');

        this.closeButton.addEventListener('click', this._onCloseButtonClick.bind(this));
        this.placemark.addEventListener('click', this._onCloseButtonClick.bind(this));
        this._applyElementOffset();
      },

      clear() {
        this.constructor.superclass.clear.call(this);
      },

      _onCloseButtonClick(e) {
        e.preventDefault();
        this.events.fire('userclose');
      },

      getClientBoundingRect() {
        if (this.element) {
          const left = this.element.offsetLeft;
          const top = this.element.offsetTop;
          return [
            [left, top], [
              left + this.element.offsetWidth,
              top + this.element.offsetHeight + this.tail.offsetHeight,
            ],
          ];
        }

        // in case if this function is called before this.element is defined
        return [[0, 0], [this.offsetWidth, this.offsetHeight]];
      },

      _applyElementOffset() {
        if (this.element) {
          this.element.style.left = `${-(this.element.offsetWidth / 2)}px`;
          this.element.style.top = `${-this.element.offsetHeight - this.tail.offsetHeight + 22}px`;
        }
      },
    });
  }

  showObject(id) {
    this.objects[id].balloon.open();
    this.scrollToMap();
  }
}

class GoogleMap extends Map {
  constructor(...args) {
    super(...args);
    this.provider = window.google.maps;
    this.objects = {};
  }

  init(element, addresses) {
    // get all addressses coords firstly
    if (this.provider) {
      Promise.all(
        addresses.map((address) => this.getCoords(address)),
      ).then((addressesWithCoords) => {
        const bounds = new this.provider.LatLngBounds();

        this.map = new this.provider.Map(q('#place-map'), {
          center: this.googlifyCoords(addressesWithCoords[0].coords),
          zoom: DEFAULT_ZOOM,
          mapTypeId: this.provider.MapTypeId.ROADMAP,
        });

        addressesWithCoords.forEach((address) => {
          const coords = this.googlifyCoords(address.coords);
          bounds.extend(coords);
          this.createObject(address);
        });

        if (addressesWithCoords.length > 1) {
          this.map.fitBounds(bounds);
        }
      });
    }
  }

  createObject(address) {
    const coords = this.googlifyCoords(address.coords);

    const marker = new this.provider.Marker({
      position: coords,
      map: this.map,
      icon: {
        url: `${kudago.staticBaseUrl}img/pins.png`,
        size: new this.provider.Size(36, 37),
        origin: new this.provider.Point(0, 0),
        anchor: new this.provider.Point(12, 37),
      },
    });

    const infowindow = new this.provider.InfoWindow({
      position: coords,
      content: `
        <div class="map-balloon-content">
          <p class="map-balloon-content-title">${address.title}</p>
          <p class="map-balloon-content-description">${address.address}</p>
        </div>
      `,
    });

    infowindow.addListener('closeclick', () => {
      marker.setVisible(true);
    });

    marker.addListener('click', () => {
      this.showObject(address.id);
    });

    this.objects[address.id] = { marker, infowindow };
    return marker;
  }

  getCoordsFromAddress(address, callback) {
    const geocoder = new this.provider.Geocoder();

    geocoder.geocode({
      region: address.location,
      address: address.address,
    }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK) {
        callback(results[0].geometry.location);
      } else {
        throw status;
      }
    });
  }

  googlifyCoords(coords) {
    if (coords instanceof this.provider.LatLng) {
      return coords;
    }

    return new this.provider.LatLng(...coords);
  }

  showObject(id) {
    forEach(this.objects, (item) => {
      const { marker, infowindow } = item;
      infowindow.close();
      marker.setVisible(true);
    });

    const { marker, infowindow } = this.objects[id];
    infowindow.open(this.map);
    marker.setVisible(false);
    this.scrollToMap();
  }
}

module.exports = {
  YandexMap,
  GoogleMap,
};
